import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Modal, message, Spin, Button,
} from 'antd';
import { FileTextOutlined, LoadingOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import numeral from 'numeral';
import wx from 'weixin-js-sdk';
import { fellowHttpRequest, httpRequest } from '@/utils';
import { rewriteHost } from '@/utils/appConfig';
import usePendingPoll from '@/hooks/usePendingPoll';
import { amountFormat, localStorageKeys } from '@/constants';
import authStore from '@/stores/auth';
import { MyModal } from '@/components/popup';
import { RetryView, TIMEOUT_MESSAGE, ERROR_MESSAGE } from '@/components';
import "./index.less";

import agreement from '../images/agreement.png';

import { MyFooter } from './styles';
import MiniLaunch from "@modules/home/home/MiniLaunch";
import TencentBannerAd from '@/components/tencentBannerAd';
import { fetchH5ResignationConfig } from '@/modules/resignation/services';
import WelcomeHeader from './WelcomeHeader';

const Home = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isCardBind, setIsCardBind] = useState(false);
  const [noBindCorporationIds, setNoBindCorporationIds] = useState([]);
  const isAuth = sessionStorage.getItem(localStorageKeys.bindState) === 'true';
  const [balance, setBalance] = useState(0);
  const token = sessionStorage.getItem(localStorageKeys.token);
  const [modalVisible, setModalVisible] = useState({
    privacyAgreement: localStorage.getItem(localStorageKeys.privacyAgreed) !== 'true',
  });

  const getStaffInfo = async () => {
    try {
      const res = await fellowHttpRequest.get('/client/h5/fellow/info');
      if (res.code === 0) {
        authStore.setProfile({
          idNo: res.data?.idNo,
          name: res.data?.name,
        });
        authStore.setCoverPoint({
          userId: res.data?.userId,
        });
      } else {
        throw new Error(res.msg);
      }
    } catch (err) {
      message.error(err.message);
    }
  };

  // 通过 usePendingPoll 驱动 /payrollCard/auth：Staff race window 期间返回 staff_pending errorCode 时，
  // 自动每 2s polling 直到 ready 或 30s 超时。enabled 仅在 token + 已激活时打开，避免未激活用户无效请求。
  const cardBindFetcher = useCallback(
    () => httpRequest.get('/client/wxPayRollCard/payrollCard/auth'),
    [],
  );
  const {
    status: cardBindStatus,
    data: cardBindData,
    retry: retryCardBind,
  } = usePendingPoll(cardBindFetcher, { enabled: !!token && isAuth });

  // ready 时同步到本地状态，沿用现有 isCardBind / noBindCorporationIds 驱动渲染
  useEffect(() => {
    if (cardBindStatus !== 'ready' || !cardBindData) return;
    setIsCardBind(cardBindData.auth);
    if (cardBindData.noAuthCorporationIds && cardBindData.noAuthCorporationIds.length === 1) {
      setNoBindCorporationIds(cardBindData.noAuthCorporationIds);
    }
  }, [cardBindStatus, cardBindData]);

  // 小程序回跳后由 Modal 回调触发，主动重试
  const getCardBindStatus = retryCardBind;

  const getBalance = async () => {
    try {
      const res = await httpRequest.get('/client/wxPayRollCard/withdrawInfo');
      if (res.code === 0) {
        setBalance(res?.data?.totalAmt);
      } else {
        throw new Error(res.msg);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const init = async () => {
    try {
      setLoading(true);
      authStore.setFeatures({
        h5ResignationEnabled: false,
        h5ResignationLoaded: false,
      });
      const res = await httpRequest.get('/client/index/info');
      if (res.code === 0) {
        authStore.setCompany({
          tenantName: res.data?.tenantName,
        });
        sessionStorage.setItem(localStorageKeys.tenantId, res.data?.tenantId);
        sessionStorage.setItem(localStorageKeys.openId, res.data?.openId);
        try {
          const resignationConfig = await fetchH5ResignationConfig();
          authStore.setFeatures({
            h5ResignationEnabled:
              resignationConfig?.code === 0 && resignationConfig?.data?.enabled === true,
            h5ResignationLoaded: true,
          });
        } catch {
          authStore.setFeatures({
            h5ResignationEnabled: false,
            h5ResignationLoaded: true,
          });
        }
        if (isAuth) {
          getStaffInfo();
          getBalance();
          // /payrollCard/auth 由 usePendingPoll 自动驱动，无需在此显式调用
        }
      } else {
        throw new Error(res.msg);
      }
    } catch (err) {
      message.error(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      init();
    }
  }, [token]);

  const handleModalVisibleChange = (name, visible) => {
    setModalVisible({
      ...modalVisible,
      [name]: visible,
    });
  };

  //信息管理
  const handleIdentityClick = (link) => {
    if (!isAuth) {
      message.warning('您的账号还未激活，请点击[去激活]完成实名认证！');
      return;
    }
    navigate(link);
  };

  //微工卡
  const handleCardClick = (link) => {
    if (!isAuth) {
      message.warning('您的账号还未激活，请点击[去激活]完成实名认证！');
      return;
    }
    navigate(link);
  };

  //工资条
  const handleSalary = (link) => {
    if (!isAuth) {
      message.warning('您的账号还未激活，请点击[去激活]完成实名认证！');
      return;
    }
    navigate(link);
  };

  //电子合同
  const handleContract = (link) => {
    if (!isAuth) {
      message.warning('您的账号还未激活，请点击[去激活]完成实名认证！');
      return;
    }
    navigate(link);
  };

  //投诉反馈
  const handleFeedback = (link) => {
    if (!isAuth) {
      message.warning('您的账号还未激活，请点击[去激活]完成实名认证！');
      return;
    }
    navigate(link);
  };

  const handleResignation = (link) => {
    if (!isAuth) {
      message.warning('您的账号还未激活，请点击[去激活]完成实名认证！');
      return;
    }
    navigate(link);
  };

  const handleMenuClick = (type) => {
    switch (type) {
      case 'ID':
        handleIdentityClick('/manager');
        break;
      case 'CARD':
        handleCardClick('/wxPayrollCard');
        break;
      case 'SALARY':
        handleSalary('/salary');
        break;
      case 'CONTRACT':
        handleContract('/contract');
        break;
      case 'FEEDBACK':
        handleFeedback('/feedback');
        break;
      case 'RESIGNATION':
        handleResignation('/resignation');
        break;
      default:
        break;
    }
  };

  const handlePrivacyModalConfirm = () => {
    handleModalVisibleChange('privacyAgreement', false);
    localStorage.setItem(localStorageKeys.privacyAgreed, 'true');
  };

  const handleDisagree = () => {
    wx.ready(() => {
      wx.closeWindow();
    });
  };

  //收支明细
  const handleDetailed = () => {
    if (!isAuth) {
      message.warning('您的账号还未激活，请点击[去激活]完成实名认证！');
      return;
    }
    if (!isCardBind) {
      message.warning('您还有微工卡未认证，请点击[去认证]完成认证！');
      return;
    }

    navigate('/detailed');
  };

  //提现
  const handleWithdraw = () => {
    if (!isAuth) {
      message.warning('您的账号还未激活，请点击[去激活]完成实名认证！');
      return;
    }
    if (!isCardBind) {
      message.warning('您还有微工卡未认证，请点击[去认证]完成认证！');
      return;
    }

    navigate('/wxPayrollCard');
  };

  //去激活
  const gotoActivate = () => navigate('/identity');

  //去认证
  const gotoAuthCard = () => {
    navigate('/wxPayrollCard');
  }

  //打开小程序后的回调
  const gotoAuthCardCallback = () => {
    Modal.confirm({
      title: '实名认证已完成?',
      okText: '是',
      cancelText: '否',
      onOk: getCardBindStatus,
      onCancel: getCardBindStatus,
    });
  };

  return (
      <Spin spinning={loading}>
        <div className="home-container">
          <WelcomeHeader name={authStore.profile?.name} />
          <div className="bottom-box">
            <div className="money-box">
              <div className="content-box">
                {isAuth && cardBindStatus === 'loading' ? (
                  <div className='auth-box' style={{ justifyContent: 'center' }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 22, color: '#1CAEFB' }} spin />} />
                  </div>
                ) : isAuth && cardBindStatus === 'pending' ? (
                  <div className='auth-box'>
                    <div className='auth-title-box'>
                      <text className='auth-title'>账户准备中</text>
                      <text style={{ fontSize: 14, color: '#9aa4af', lineHeight: '20px' }}>最多等 30 秒</text>
                    </div>
                    <div className='auth-button-box'>
                      <Spin indicator={<LoadingOutlined style={{ fontSize: 22, color: '#1CAEFB' }} spin />} />
                    </div>
                  </div>
                ) : isAuth && cardBindStatus === 'timeout' ? (
                  <div className='auth-box' style={{ textAlign: 'center' }}>
                    <RetryView message={TIMEOUT_MESSAGE} onRetry={retryCardBind} />
                  </div>
                ) : isAuth && cardBindStatus === 'error' ? (
                  <div className='auth-box' style={{ textAlign: 'center' }}>
                    <RetryView message={ERROR_MESSAGE} onRetry={retryCardBind} />
                  </div>
                ) : isAuth ? (isCardBind ? <div className='auth-box'>
                  <div className='auth-title-box'>
                    <text className='auth-title'>账户余额(元)</text>
                    <text className='auth-text'>{numeral(balance).format(amountFormat)}</text>
                    <text className='get-money-text' onClick={handleDetailed}>收支明细</text>
                  </div>
                  <div className='auth-button-box'>
                    <Button type="primary" color="#409eff" onClick={handleWithdraw}
                            style={{width: '100px', fontSize: '18px', lineHeight: '24px'}} size="middle" shape="round">
                      提现
                    </Button>
                  </div>
                </div> : <div className='no-auth-box'>
                  <div className='no-auth-title-box'>
                    <text className='no-auth-title'>认证微工卡</text>
                    <text className='no-auth-text'>认证微工卡后已发放至账户的余额可提现至微工卡</text>
                  </div>
                  <div className='no-auth-button-box'>
                    {
                      //如果微工卡数量为0，不显示任何按钮
                      0 === noBindCorporationIds?.length? (
                          <></>
                      ):(
                          //如果只有一个未认证的，直接跳转小程序去认证
                          1 === noBindCorporationIds?.length?(
                              <>
                                <Button type="primary" color="#409eff" ghost style={{width: '100px'}}
                                        size="small" shape="round">
                                  去认证
                                </Button>
                                <MiniLaunch corporationId={noBindCorporationIds[0]} onLaunched={gotoAuthCardCallback}/>
                              </>
                          ):(
                              //其他情况跳转到微工卡列表页面
                              <Button type="primary" color="#409eff" onClick={gotoAuthCard} ghost style={{width: '100px'}}
                                      size="small" shape="round">
                                去认证
                              </Button>
                          ))
                    }
                  </div>
                </div>) : <div className='no-auth-box'>
                  <div className='no-auth-title-box'>
                    <text className='no-auth-title'>激活微信账号，开通账户</text>
                    <text className='no-auth-text'>激活微信账号后已发放至账户的余额会自动显示</text>
                  </div>
                  <div className='no-auth-button-box'>
                    <Button type="primary" color="#409eff" onClick={gotoActivate} ghost style={{width: '100px'}}
                            size="small" shape="round">
                      去激活
                    </Button>
                  </div>
                </div>}
              </div>
            </div>
            <div className="function-box">
              <div onClick={() => handleMenuClick('ID')} className="content-box">
                <div className="inner-content-box-info">
                  <span className="text">信息管理</span>
                  <div className="info-image"></div>
                </div>
              </div>
              <div onClick={() => handleMenuClick('CARD')} className="content-box">
                <div className="inner-content-wgk-info">
                  <span className="text">微工卡</span>
                  <div className="info-image"></div>
                </div>
              </div>
              <div onClick={() => handleMenuClick('SALARY')} className="content-box">
                <div className="inner-content-salary-info">
                  <span className="text">工资条</span>
                  <div className="salary-image"></div>
                </div>
              </div>
              <div onClick={() => handleMenuClick('CONTRACT')} className="content-box">
                <div className="inner-content-contract-info">
                  <span className="text">电子合同</span>
                  <div className="contract-image"></div>
                </div>
              </div>
              <div onClick={() => handleMenuClick('FEEDBACK')} className="content-box">
                <div className="inner-content-feedback-info">
                  <span className="text">投诉反馈</span>
                  <div className="feedback-image"></div>
                </div>
              </div>
              {authStore.features.h5ResignationLoaded
                && authStore.features.h5ResignationEnabled && (
                <div
                  aria-label="离职申请"
                  onClick={() => handleMenuClick('RESIGNATION')}
                  className="content-box"
                >
                  <div className="inner-content-resignation-info">
                    <span className="text">离职申请</span>
                    <FileTextOutlined className="resignation-icon" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <TencentBannerAd />
          <MyFooter>
            <ul>
              <li className="home">
                <p className="active">首页</p>
              </li>
              <Link style={{ color: '#9AA4AF', width: '50%', display: 'block' }} to="advance">
                <li className="advance">
                  <p>预支</p>
                </li>
              </Link>
            </ul>
          </MyFooter>
        </div>
        <Modal
            visible={modalVisible.privacyAgreement}
            modalRender={() => (
                <MyModal
                    icon={agreement}
                    okText="确定"
                    noText="取消"
                    title="用户协议"
                    paragraph={() => (
                        <>
                          欢迎使用“云彩人资科研”，我们非常重视您的个人信息和隐私保护。在您使用“云彩人资科研”服务之前。请仔细阅读
                          <a href={rewriteHost("https://portal.ycrzky.com/privacyPolicy")}>《云彩人资科研隐私政策》</a>
                          和
                          <a href={rewriteHost("https://portal.ycrzky.com/userAgreement")}>《云彩人资科研用户协议》</a>
                          ，我们将严格按照您同意的条款使用您的个人信息，以便为您提供服务。
                        </>
                    )}
                    text=" 如您同意此政策，请点击“同意”并开始使用我们的产品和服务，我们将尽全力保护您的个人信息安全。"
                    onNo={handleDisagree}
                    onOk={handlePrivacyModalConfirm}
                    contentStyle={{
                      paddingTop: '60px',
                      background: 'linear-gradient(180deg, #E9F7FF 0%, #FFFFFF 100%)',
                    }}
                />
            )}
        />
      </Spin>
  );
};

export default observer(Home);
